"use client";

import { useState, useEffect } from "react";
import {
  X,
  ZoomIn,
  ZoomOut,
  Download,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { Dialog, DialogPortal } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";

interface PdfViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pdfUrl: string;
  title?: string;
}

export function PdfViewerDialog({
  open,
  onOpenChange,
  pdfUrl,
  title,
}: PdfViewerDialogProps) {
  const [zoom, setZoom] = useState<number>(100);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 50));
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = title ? `${title}.pdf` : "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setZoom(100);
      setIsFullscreen(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-all duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 bg-transparent transition-all duration-200 ease-in-out",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-98 data-[state=open]:zoom-in-98",
            isFullscreen
              ? "inset-4"
              : "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] w-[95vw] max-w-6xl h-[90vh]"
          )}
        >
          {/* Close button */}
          <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-full bg-slate-900/80 backdrop-blur-md p-2.5 text-white opacity-70 ring-offset-background transition-all duration-200 hover:opacity-100 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          {/* Title and Controls Bar */}
          <div className="absolute top-4 left-4 right-20 z-10 flex items-center justify-between gap-4">
            {/* Title */}
            {title && (
              <div className="bg-slate-900/80 backdrop-blur-md rounded-full px-4 py-2 border border-white/10">
                <h3 className="text-white font-medium text-sm truncate max-w-[300px]">
                  {title}
                </h3>
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center gap-2 bg-gradient-to-r from-slate-900/95 to-slate-800/95 backdrop-blur-md rounded-full px-3 py-2 shadow-2xl border border-white/10">
              {/* Zoom Controls */}
              <Button
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/10 hover:text-white disabled:opacity-30 rounded-full transition-all duration-200"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>

              <span className="min-w-[60px] text-center text-sm font-medium text-white">
                {zoom}%
              </span>

              <Button
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/10 hover:text-white disabled:opacity-30 rounded-full transition-all duration-200"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>

              <div className="h-5 w-px bg-white/20 mx-1" />

              {/* Fullscreen Toggle */}
              <Button
                onClick={toggleFullscreen}
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/10 hover:text-white rounded-full transition-all duration-200"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>

              {/* Download
              <Button
                onClick={handleDownload}
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/10 hover:text-white rounded-full transition-all duration-200"
                title="Download PDF"
              >
                <Download className="h-4 w-4" />
              </Button> */}
            </div>
          </div>

          {/* PDF Container */}
          <div className="h-full w-full pt-16 pb-4 px-4">
            <div
              className="w-full h-full bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 shadow-2xl overflow-hidden transition-all duration-300"
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: "center top",
              }}
            >
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0"
                title={title || "PDF Viewer"}
                style={{
                  background: "white",
                }}
              />
            </div>
          </div>

          {/* Keyboard Hints */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/80 backdrop-blur-md rounded-full px-4 py-2 border border-white/10">
            <div className="flex items-center gap-4 text-xs text-white/70">
              <span>ESC to close</span>
              <span>•</span>
              <span>Click +/- to zoom</span>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
